/**
 * Rogue Admin — API types. The single place where response shapes live.
 *
 * Two kinds of types are in this file:
 *
 *  CONTRACT     Defined by admin-api.openapi.yaml or by existing backend enums (rogue/backend).
 *               Safe to rely on.
 *
 *  VIEW MODELS  Response shapes of the /admin/v1 operations. The backend now serves every one
 *               of them (rogue/backend/src/modules/admin builds exactly these
 *               shapes); they are kept here by hand until the contract carries full schemas.
 *               Components depend on them only through src/api/queries + src/api/mutations.
 *               Unknown enum values must render gracefully (see src/lib/status).
 */

// ═══ CONTRACT ═══════════════════════════════════════════════════════════════

/** GET /health */
export interface Liveness {
  status: "ok";
  service: string;
}

/** GET /ready (200 or 503) */
export interface Readiness {
  status: "ready" | "not_ready";
  checks: { database: boolean; redis: boolean; aiService: boolean };
}

/** Better Auth user */
export interface AuthUser {
  id: string;
  name: string;
  email: string;
  emailVerified?: boolean;
  image?: string | null;
  createdAt?: string;
}

/** GET /api/auth/get-session */
export interface AuthSession {
  session: { id: string; userId: string; expiresAt: string; activeOrganizationId?: string | null };
  user: AuthUser;
}

// Backend enums (rogue/backend/src/db/schema) — established by the Rogue architecture.
export const OPPORTUNITY_STAGES = [
  "NEW",
  "IDENTIFYING",
  "ENRICHING",
  "VALUED",
  "QUALIFYING",
  "ENGAGED",
  "OFFER_SENT",
  "NEGOTIATING",
  "OFFER_ACCEPTED",
  "APPOINTMENT_SET",
  "APPOINTMENT_CONFIRMED",
  "SHOWED",
  "APPRAISED",
  "PURCHASED",
] as const;
export type OpportunityStage = (typeof OPPORTUNITY_STAGES)[number];

export const OPPORTUNITY_STATUSES = ["OPEN", "DISQUALIFIED", "OPTED_OUT", "UNRESPONSIVE", "LOST", "EXPIRED", "DUPLICATE"] as const;
export type OpportunityStatus = (typeof OPPORTUNITY_STATUSES)[number];

/** Where a lead came from (Rogue's answer to "how did you get my number?" depends on it). */
export const OPPORTUNITY_SOURCES = ["KBB_ICO", "CRM", "FACEBOOK"] as const;
export type OpportunitySource = (typeof OPPORTUNITY_SOURCES)[number];

export const CONVERSATION_AI_STATUSES = ["IDLE", "QUEUED", "PROCESSING", "FAILED"] as const;
export type ConversationAiStatus = (typeof CONVERSATION_AI_STATUSES)[number];
export type MessageAiStatus = "PENDING" | "PROCESSING" | "PROCESSED" | "FAILED" | "SKIPPED";
export type DeliveryStatus = "NOT_SENT" | "QUEUED" | "SENT" | "DELIVERED" | "READ" | "FAILED";
export type MessageDirection = "INBOUND" | "OUTBOUND";
export type MessageAuthorType = "SELLER" | "AI" | "USER" | "SYSTEM";

export const AI_RUN_STATUSES = ["QUEUED", "PROCESSING", "COMPLETED", "FAILED", "STALE", "CANCELLED"] as const;
export type AiRunStatus = (typeof AI_RUN_STATUSES)[number];
export type AiRunTrigger = "INBOUND_MESSAGE" | "STALE_RERUN" | "MANUAL" | "FIRST_CONTACT";

export type AppointmentStatus = "PROPOSED" | "SCHEDULED" | "CONFIRMED" | "SHOWED" | "NO_SHOW" | "CANCELLED";
export type OfferStatus = "RECOMMENDED" | "PENDING_APPROVAL" | "APPROVED" | "BLOCKED" | "SENT" | "ACCEPTED" | "DECLINED" | "WITHDRAWN";
export type StoreRole = "STORE_MANAGER" | "BUYER" | "BDC" | "APPRAISER" | "VIEWER";
export type OrgRole = "OWNER" | "ADMIN" | "GROUP_MANAGER" | "MEMBER";

/** The known conversation-ai BullMQ queue (rogue/backend/src/queues). */
export const KNOWN_QUEUES = ["conversation-ai"] as const;

// ═══ VIEW MODELS (served by /admin/v1) ═══════════════════════════════════════

// ── common list shapes ─────────────────────────────────────────────────────
export type SortOrder = "asc" | "desc";

/** Server-side paging, filtering and sorting. The UI never loads full datasets. */
export interface ListQuery<F extends object = Record<string, never>> {
  q?: string;
  page: number;
  pageSize: number;
  sort?: string;
  order?: SortOrder;
  filters: F;
}

export interface Page<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
}

export interface EntityRef {
  id: string;
  name: string;
}

export type DateRange = "24h" | "7d" | "30d" | "90d";

// ── admin identity & permissions ───────────────────────────────────────────
export const ADMIN_ROLES = ["SUPER_ADMIN", "ADMIN", "SUPPORT", "OPERATIONS", "FINANCE", "READ_ONLY"] as const;
export type AdminRole = (typeof ADMIN_ROLES)[number];

export interface AdminIdentity {
  userId: string;
  name: string;
  email: string;
  role: AdminRole | null;
  /** When the backend sends explicit permissions they win over the role mapping. */
  permissions?: string[];
  /** Signed in with a temporary password: the console only allows setting a new one. */
  mustChangePassword?: boolean;
  /** Development deployment: dev tools (SMS simulator) exist on the backend. */
  devTools?: boolean;
  /** Your Slack member id (tagged in handoff alerts). */
  slackUserId?: string | null;
}

// ── platform staff (this console's own accounts) ───────────────────────────
export const STAFF_STATUSES = ["ACTIVE", "DISABLED"] as const;
export type StaffStatus = (typeof STAFF_STATUSES)[number];

export interface StaffRow {
  id: string;
  name: string;
  email: string;
  role: AdminRole;
  /** Slack member id (U…/W…), tagged in handoff alerts. */
  slackUserId?: string | null;
  status: StaffStatus;
  disabledAt: string | null;
  /** Has a temporary password (new or reset) they haven't replaced yet. */
  mustChangePassword: boolean;
  lastSignInAt: string | null;
  activeSessions: number;
  createdAt: string;
}

export interface StaffSession {
  id: string;
  createdAt: string;
  lastActiveAt: string;
  expiresAt: string;
  ipAddress: string | null;
  userAgent: string | null;
  /** The session making this request. */
  current: boolean;
}

export interface StaffDetail extends StaffRow {
  sessions: StaffSession[];
  recentActivity: AuditEvent[];
}

export interface StaffFilters {
  role?: AdminRole;
  status?: StaffStatus;
}

/** Create / reset-password response: the temporary password is shown once and never again. */
export interface StaffCredentials {
  staff: StaffDetail;
  temporaryPassword: string;
}

// ── system ─────────────────────────────────────────────────────────────────
export const HEALTH_STATUSES = ["OPERATIONAL", "DEGRADED", "UNAVAILABLE", "UNKNOWN"] as const;
export type HealthStatus = (typeof HEALTH_STATUSES)[number];
export type HealthComponentId = "api" | "database" | "redis" | "aiService" | "twilio" | "queue";

export interface HealthComponent {
  id: HealthComponentId;
  name: string;
  status: HealthStatus;
  /** Where the status came from, e.g. "GET /ready" or "No health signal in the API contract". */
  source: string;
  detail?: string | null;
  checkedAt: string | null;
}

export interface SystemHealth {
  overall: HealthStatus;
  components: HealthComponent[];
  checkedAt: string;
}

export interface SystemMetric {
  key: string;
  label: string;
  value: number | null;
  unit: "ms" | "percent" | "count" | "per_min";
  window: string;
}

export type ErrorSeverity = "CRITICAL" | "ERROR" | "WARNING";
export const ERROR_STATUSES = ["OPEN", "INVESTIGATING", "RESOLVED", "IGNORED"] as const;
export type ErrorStatus = (typeof ERROR_STATUSES)[number];

export interface ErrorGroup {
  id: string;
  lastSeenAt: string;
  firstSeenAt: string;
  service: string;
  message: string;
  code: string | null;
  organization: EntityRef | null;
  store: EntityRef | null;
  severity: ErrorSeverity;
  status: ErrorStatus;
  occurrences: number;
  sample?: { requestId: string | null; aiRunId: string | null; conversationId: string | null; jobId: string | null } | null;
}

export interface ErrorFilters {
  service?: string;
  severity?: ErrorSeverity;
  status?: ErrorStatus;
  organizationId?: string;
}

export interface SystemEvent {
  id: string;
  at: string;
  service: string;
  type: string;
  message: string;
  severity: "INFO" | "WARNING" | "ERROR";
}

/** One step in an investigation timeline — only backend-recorded events with backend timestamps. */
export interface TimelineEvent {
  id: string;
  at: string;
  type: string;
  label: string;
  service: string;
  status?: "OK" | "WARNING" | "ERROR";
  refs?: { aiRunId?: string; jobId?: string; messageId?: string; offerId?: string; appointmentId?: string; purchaseId?: string };
}

// ── overview ───────────────────────────────────────────────────────────────
export interface OverviewMetrics {
  organizations: number | null;
  stores: number | null;
  activeUsers: number | null;
  activeOpportunities: number | null;
  activeConversations: number | null;
  aiJobsProcessing: number | null;
  aiFailures: number | null;
  queueBacklog: number | null;
  appointmentsToday: number | null;
  purchasesToday: number | null;
  integrationFailures: number | null;
  /** The window aiFailures / integrationFailures cover, e.g. "24h". */
  window: string;
}

// ── organizations ──────────────────────────────────────────────────────────
export const ORGANIZATION_STATUSES = ["ACTIVE", "SUSPENDED"] as const;
export type OrganizationStatus = (typeof ORGANIZATION_STATUSES)[number];

export interface OrganizationRow {
  id: string;
  name: string;
  slug: string;
  storeCount: number;
  userCount: number;
  activeOpportunities: number;
  plan: string | null;
  status: OrganizationStatus;
  createdAt: string;
  lastActivityAt: string | null;
}

export interface OrganizationDetail extends OrganizationRow {
  suspendedAt: string | null;
  suspensionReason: string | null;
  settings: Record<string, string | number | boolean | null>;
  counts: { conversations: number; purchases30d: number; aiRuns24h: number; aiFailures24h: number; integrations: number };
  integrations: Array<{ id: string; name: string; status: IntegrationStatus; lastSyncAt: string | null }>;
  /** CRM email: the organization's switch, whether the platform can send, and active stores without an email. */
  crmEmail?: { enabled: boolean; configured: boolean; storesWithoutEmail: EntityRef[] };
}

/** Plan names used by filters and the edit form. TODO: BACKEND API CONTRACT REQUIRED (plan catalog). */
export const PROVISIONAL_PLANS = ["Starter", "Growth", "Enterprise"] as const;

export interface OrganizationFilters {
  status?: OrganizationStatus;
  plan?: string;
  createdFrom?: string;
  createdTo?: string;
  minStores?: number;
  activity?: "active_24h" | "active_7d" | "inactive_30d";
}

export interface OrganizationPatch {
  name?: string;
  plan?: string | null;
  /** Offer API: Rogue gets its max offer from the organization's offer API integrations. */
  offerApi?: boolean;
  /** CRM email: booked appointments are emailed to the store's CRM (ADF). */
  crmEmail?: boolean;
}

// ── stores ─────────────────────────────────────────────────────────────────
/** SUSPENDED comes from the organization; INACTIVE means the store itself was deactivated. */
export const STORE_STATUSES = ["ACTIVE", "INACTIVE", "SUSPENDED"] as const;
export type StoreStatus = (typeof STORE_STATUSES)[number];

export interface StoreRow {
  id: string;
  name: string;
  organization: EntityRef;
  brand: string | null;
  /** "City, ST" */
  location: string | null;
  userCount: number;
  buyerCount: number;
  activeOpportunities: number;
  appointments7d: number;
  purchases30d: number;
  status: StoreStatus;
}

export interface StoreMembership {
  userId: string;
  name: string;
  email: string;
  role: StoreRole;
  /** Other stores this person also belongs to (buyers can work several rooftops). */
  otherStoreCount: number;
}

/** SMS providers: a store has a number with each it uses; an opportunity texts through one. */
export const SMS_PROVIDERS = ["TWILIO", "INFOBIP", "TELNYX"] as const;
export type SmsProvider = (typeof SMS_PROVIDERS)[number];

export interface StoreDetail extends StoreRow {
  slug: string;
  timezone: string;
  /** E.164 numbers sellers text, one per provider (`phoneNumber` is the Twilio one). */
  phoneNumber: string | null;
  infobipNumber?: string | null;
  telnyxNumber?: string | null;
  /** The provider new leads text through (absent on older servers). */
  defaultSmsProvider?: SmsProvider;
  address: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  /** Free-form location detail, e.g. "located inside Long Beach BMW". */
  extra: string | null;
  email: string | null;
  /** Main store line for people (not the SMS number). */
  contactPhone: string | null;
  deactivatedAt: string | null;
  deactivationReason: string | null;
  createdAt: string;
  members: StoreMembership[];
  /** The store policy (the backend sends every section; the console uses the offer policy). */
  policy: StoreOfferPolicy;
  rules: Array<{ key: string; label: string; value: string }>;
  integrations: Array<{ id: string; name: string; status: IntegrationStatus }>;
  /** The organization's Offer API switch. */
  offerApi?: boolean;
  /** The organization's offer integrations and whether this store is linked to each. */
  offerIntegrations?: StoreOfferIntegration[];
}

/** What a store can require before an offer, besides year/make/model (always required). */
export const OFFER_READINESS_FIELDS = ["mileage", "vin", "title_status", "condition", "photos"] as const;
export type OfferReadinessField = (typeof OFFER_READINESS_FIELDS)[number];
export type HandoffAssignment = "ROUND_ROBIN" | "STORE_MANAGER" | "NONE";

/** Offer policy sections of the store policy (money in cents). Dealers can only read them. */
export interface StoreOfferPolicy {
  offers: {
    /** API valuations: the max offer is this % of the valuation. */
    maxPercentOfValuation: number;
    incrementCents: number;
    autoApproveMaxCents: number;
    valuationMaxAgeDays: number;
  };
  negotiation: {
    requiredInfo: OfferReadinessField[];
    /** Seller's price more than this % above the max: open at the max. */
    openAtCeilingGapPercent: number;
    /** More than this % above the max (or unknown): ask their price first, once. */
    askPriceGapPercent: number;
    /** Otherwise open this far below the max. */
    openingBelowCeilingCents: number;
    maxStepCents: number;
    handoffGapCents: number;
  };
  /** notifyAdminId: a Rogue admin also alerted on Slack on every handoff (absent on older servers). */
  handoff: { assignment: HandoffAssignment; notifyAdminId?: string | null };
  /** Appointment settings (admin-only; dealers read them). Absent on older servers. */
  appointments?: AppointmentPolicy;
}

/** When and how a store takes appointments, and the text sellers get. Store-local times. */
export interface AppointmentPolicy {
  /** Rogue may book on its own (else it proposes and a person confirms). */
  aiCanSchedule: boolean;
  minLeadMinutes: number;
  maxDaysAhead: number;
  /** Working hours per weekday. */
  hours?: Record<"mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun", { open: boolean; start: string; end: string }>;
  slotMinutes?: number;
  /** The store's note for sellers ({{note}}). */
  note?: string;
  /** Who sellers ask for on arrival ({{ask_for}}). */
  askFor?: string;
  /** The text sent when staff book or reschedule ({{variables}}). */
  messageTemplate?: string;
}

export interface StorePolicyPatch {
  offers?: Partial<StoreOfferPolicy["offers"]>;
  negotiation?: Partial<StoreOfferPolicy["negotiation"]>;
  handoff?: Partial<StoreOfferPolicy["handoff"]>;
  /** Working hours merge day by day. */
  appointments?: Partial<AppointmentPolicy>;
}

export interface StoreFilters {
  organizationId?: string;
  status?: StoreStatus;
  brand?: string;
}

/** Create / edit store. Address, city, state (US code), ZIP and a US time zone are required. */
export interface StoreInput {
  name: string;
  address: string;
  city: string;
  /** Two-letter US state code (see src/lib/us-locations.ts). */
  state: string;
  postalCode: string;
  timezone: string;
  extra?: string | null;
  email?: string | null;
  contactPhone?: string | null;
  phoneNumber?: string | null;
  infobipNumber?: string | null;
  telnyxNumber?: string | null;
}

export interface NewOrganization {
  name: string;
  slug?: string;
  plan?: string | null;
}

// ── users ──────────────────────────────────────────────────────────────────
export const USER_STATUSES = ["ACTIVE", "SUSPENDED", "INVITED"] as const;
export type UserStatus = (typeof USER_STATUSES)[number];

export interface UserRow {
  id: string;
  /** Full display name ("first last" for accounts created with name parts). */
  name: string;
  firstName?: string | null;
  lastName?: string | null;
  email: string;
  phone?: string | null;
  organizations: EntityRef[];
  storeCount: number;
  roles: string[];
  lastActiveAt: string | null;
  status: UserStatus;
  createdAt: string;
  adminRole: AdminRole | null;
}

export interface UserDetail extends UserRow {
  organizationMemberships: Array<{ organization: EntityRef; role: OrgRole }>;
  storeMemberships: Array<{ store: EntityRef; organization: EntityRef; role: StoreRole }>;
  recentActivity: AuditEvent[];
}

export interface NewDealerUser {
  firstName: string;
  lastName: string;
  email: string;
  /** Optional; normalized to E.164 by the backend and unique across dealer accounts. */
  phone?: string | null;
  role: OrgRole;
  stores: Array<{ storeId: string; role: StoreRole }>;
}

/**
 * Dealer account create / password reset. `temporaryPassword` is shown once; it is null when an
 * existing account was added to the organization (they keep their password).
 */
export interface DealerCredentials {
  user: UserDetail;
  temporaryPassword: string | null;
  created: boolean;
}

export interface UserFilters {
  organizationId?: string;
  storeId?: string;
  status?: UserStatus;
  role?: string;
}

// ── opportunities ──────────────────────────────────────────────────────────
export interface OpportunityRow {
  id: string;
  organization: EntityRef;
  store: EntityRef;
  vehicle: { id: string; title: string | null; vin: string | null } | null;
  seller: { id: string; name: string | null } | null;
  source: OpportunitySource;
  stage: OpportunityStage;
  status: OpportunityStatus;
  score: number | null;
  assignedUser: EntityRef | null;
  conversationId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface OpportunityDetail extends OpportunityRow {
  seller: { id: string; name: string | null; phone: string | null; email: string | null; optedOutAt: string | null } | null;
  vehicle: { id: string; title: string | null; vin: string | null; mileage: number | null; condition: string; titleStatus: string } | null;
  aiRuns: AiRunRow[];
  offers: Array<{ id: string; amountCents: number; status: OfferStatus; source: "AI" | "USER" | "SYSTEM"; createdAt: string }>;
  appointments: Array<{ id: string; startsAt: string; status: AppointmentStatus }>;
  purchase: { id: string; amountCents: number; purchasedAt: string } | null;
  /** How Rogue texts the seller: provider and the store's number with it (absent on older servers). */
  smsChannel?: { provider: SmsProvider; label: string; number: string | null; chosen: boolean };
}

/** Vehicle photo of an opportunity (dealer upload or seller MMS). `url` is signed and expires. */
export interface OpportunityImage {
  id: string;
  opportunityId: string;
  url: string;
  contentType: string;
  sizeBytes: number;
  source: "AI" | "USER" | "SYSTEM";
  description: string | null;
  isFeatured: boolean;
  featuredBy: "AI" | "USER" | "SYSTEM" | null;
  sourceMessageId: string | null;
  createdByUserId: string | null;
  createdAt: string;
}

// ── development tools ──────────────────────────────────────────────────────
export interface SmsSimulatorTarget {
  conversationId: string;
  seller: { name: string | null; phone: string | null; optedOut: boolean };
  store: { id: string; name: string; phoneNumber: string | null };
  /** Why this conversation can't receive simulated SMS (no numbers), else null. */
  blockedReason: string | null;
}

export type SimulatedSendMode = "split" | "single";

export interface SimulateInboundInput {
  text: string;
  images: Array<{ dataBase64: string; name?: string }>;
  mode: SimulatedSendMode;
}

export interface SimulatedDelivery {
  messageSid: string;
  kind: "SMS" | "MMS";
  body: string;
  numMedia: number;
  status: "RECORDED" | "DUPLICATE" | "UNROUTABLE";
  messageId: string | null;
  /** The form fields Twilio would POST to the inbound webhook. */
  params: Record<string, string>;
}

export interface SimulateInboundResult {
  conversationId: string;
  deliveries: SimulatedDelivery[];
}

export interface OpportunityFilters {
  organizationId?: string;
  storeId?: string;
  source?: OpportunitySource;
  stage?: OpportunityStage;
  status?: OpportunityStatus;
  assignedUserId?: string;
  createdFrom?: string;
  createdTo?: string;
}

// ── conversations ──────────────────────────────────────────────────────────
export interface ConversationRow {
  id: string;
  organization: EntityRef;
  store: EntityRef;
  seller: { id: string; name: string | null } | null;
  vehicle: { id: string; title: string | null } | null;
  opportunityId: string;
  channel: "SMS";
  aiStatus: ConversationAiStatus;
  lastMessage: { body: string; direction: MessageDirection; at: string } | null;
  lastActivityAt: string | null;
}

export interface Message {
  id: string;
  direction: MessageDirection;
  authorType: MessageAuthorType;
  body: string;
  /** Attachment types (MMS). Provider media URLs aren't exposed: they need provider credentials. */
  media?: Array<{ index: number; contentType: string | null }>;
  aiStatus: MessageAiStatus;
  deliveryStatus: DeliveryStatus;
  revision: number | null;
  aiRunId: string | null;
  errorCode: string | null;
  providerMessageId: string | null;
  createdAt: string;
  sentAt: string | null;
  deliveredAt: string | null;
}

export interface ConversationDetail extends ConversationRow {
  revision: number;
  activeAiRunId: string | null;
  control: "ROGUE" | "HUMAN" | null;
  aiRuns: AiRunRow[];
}

export interface ConversationFilters {
  organizationId?: string;
  storeId?: string;
  aiStatus?: ConversationAiStatus;
}

// ── appointments ───────────────────────────────────────────────────────────
export interface AppointmentRow {
  id: string;
  organization: EntityRef;
  store: EntityRef;
  opportunityId: string;
  seller: { id: string; name: string | null } | null;
  vehicle: { title: string | null } | null;
  startsAt: string;
  status: AppointmentStatus;
  source: "AI" | "USER" | "SYSTEM";
}

export interface AppointmentFilters {
  organizationId?: string;
  storeId?: string;
  status?: AppointmentStatus;
  date?: "today" | "upcoming" | "past";
}

// ── AI ─────────────────────────────────────────────────────────────────────
export interface AiRunRow {
  id: string;
  organization: EntityRef;
  store: EntityRef;
  opportunityId: string;
  conversationId: string;
  model: string | null;
  provider: string | null;
  promptVersion: string | null;
  status: AiRunStatus;
  trigger: AiRunTrigger;
  revision: number;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  durationMs: number | null;
  errorCode: string | null;
}

export interface AiRunDetail extends AiRunRow {
  jobId: string | null;
  queue: string | null;
  inputMessageIds: string[];
  /** Concise, business-safe audit fields from the AI result. Never chain-of-thought. */
  action: string | null;
  reason: string | null;
  nextStep: string | null;
  structuredOutput: Record<string, unknown> | null;
  error: { code: string; message: string } | null;
  supersededByRunId: string | null;
}

export interface AiSummary {
  window: string;
  runs: number | null;
  processing: number | null;
  completed: number | null;
  failed: number | null;
  stale: number | null;
  averageDurationMs: number | null;
  providerErrors: number | null;
}

export interface AiRunFilters {
  status?: AiRunStatus;
  model?: string;
  provider?: string;
  promptVersion?: string;
  organizationId?: string;
  storeId?: string;
  range?: DateRange;
}

// ── queues ─────────────────────────────────────────────────────────────────
export const JOB_STATES = ["waiting", "active", "completed", "failed", "delayed", "paused"] as const;
export type JobState = (typeof JOB_STATES)[number];

export interface QueueSummary {
  name: string;
  counts: Record<JobState, number>;
  isPaused: boolean;
  /** Queues the architecture plans but the backend doesn't run yet. */
  planned?: boolean;
  oldestWaitingAt: string | null;
}

export interface QueueJob {
  id: string;
  queue: string;
  name: string;
  state: JobState;
  attempts: number;
  maxAttempts: number;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  error: string | null;
  refs: { aiRunId: string | null; conversationId: string | null; opportunityId: string | null; organizationId: string | null };
}

export interface QueueJobDetail extends QueueJob {
  data: Record<string, unknown>;
  stacktrace: string[];
}

export interface JobFilters {
  state?: JobState;
}

// ── integrations ───────────────────────────────────────────────────────────
export type IntegrationStatus =
  | "CONNECTED"
  | "DEGRADED"
  | "FAILING"
  | "DISABLED"
  | "NOT_CONFIGURED"
  // Offer API integrations (see OfferIntegrationStatus).
  | "INACTIVE"
  | "OFFER_API_OFF"
  | "NO_STORES"
  | "READY";

export interface IntegrationSummary {
  id: string;
  name: string;
  /** e.g. "Messaging", or "Offer API · AutoNation" for offer integrations. */
  category: string;
  organizationsUsing: number;
  /** Offer API integrations belong to one organization. */
  organization?: EntityRef | null;
  status: IntegrationStatus;
  health: HealthStatus;
  lastSuccessAt: string | null;
  lastFailureAt: string | null;
}

export interface IntegrationDetail extends IntegrationSummary {
  webhook: { status: HealthStatus; lastEventAt: string | null; failures24h: number } | null;
  recentRequests: Array<{ id: string; at: string; operation: string; status: number; durationMs: number; organization: EntityRef | null }>;
  /** retryable: the failure can be sent again (CRM emails: POST /admin/v1/crm-emails/{id}/retry). */
  failures: Array<{ id: string; at: string; message: string; organization: EntityRef | null; retryable?: boolean }>;
  organizations: Array<EntityRef & { status: IntegrationStatus; lastSyncAt: string | null }>;
}

/** A CRM email (ADF lead sent to the store's email when an appointment is booked) after a retry. */
export interface CrmEmailRetryResult {
  crmEmail: { id: string; status: "PENDING" | "SENT" | "FAILED" | "SKIPPED"; error: string | null; to: string | null; sentAt: string | null } | null;
}

// ── Offer API (offer integrations) ─────────────────────────────────────────
/** A credential an offer API connector needs. Secrets are write-only. */
export interface OfferDriverField {
  key: string;
  label: string;
  secret: boolean;
  required: boolean;
  placeholder?: string;
  help?: string;
}

/** A connector ("function") an offer integration runs; the orchestrator routes by `key`. */
export interface OfferDriver {
  key: string;
  label: string;
  description: string;
  /** OFFER: a firm offer used as the max offer as-is. VALUATION: × the store's % of valuation. */
  kind: "OFFER" | "VALUATION";
  credentials: OfferDriverField[];
  externalStoreId: { label: string; required: boolean; help: string } | null;
}

/**
 * Why an offer integration does or doesn't run, first blocker first: INACTIVE (its switch is off) ·
 * OFFER_API_OFF (the organization's Offer API is off) · NO_STORES (on for no store) · FAILING ·
 * CONNECTED (it answered) · READY (on, not used by a lead yet).
 */
export type OfferIntegrationStatus = "INACTIVE" | "OFFER_API_OFF" | "NO_STORES" | "FAILING" | "CONNECTED" | "READY";

/**
 * When an integration's API offer is re-run. Defaults: mileage up 500+ or down more than 2,000
 * since the offer; or the offer expired (the API's expiry date when given, else N days).
 */
export interface OfferRerunSettings {
  mileage: { enabled: boolean; increaseMiles: number; decreaseMiles: number };
  expiry: { enabled: boolean; useProviderExpiry: boolean; days: number };
}

export interface OfferIntegrationSummary {
  id: string;
  name: string;
  driver: string;
  driverLabel: string;
  kind: "OFFER" | "VALUATION" | null;
  organization: EntityRef;
  /** The organization's Offer API switch: the integration only runs when it's on. */
  organizationOfferApi: boolean;
  active: boolean;
  status: OfferIntegrationStatus;
  storesLinked: number;
  lastSuccessAt: string | null;
  lastFailureAt: string | null;
  lastError: string | null;
  createdAt: string;
  /** Absent on older servers. */
  settings?: OfferRerunSettings;
}

/** Is it working: runs in the last 24 hours, and the latest one (from the run log). */
export interface OfferRunHealth {
  last24h: { total: number; succeeded: number; declined: number; failed: number; avgDurationMs: number | null };
  lastRun: { status: OfferRunStatus; startedAt: string } | null;
}

export interface OfferIntegrationDetail extends OfferIntegrationSummary {
  /** Absent on older servers. */
  health?: OfferRunHealth;
  description: string | null;
  externalStoreIdField: OfferDriver["externalStoreId"];
  credentialsReadable: boolean;
  /** `value` only for non-secret fields; secrets report `set` only. */
  credentials: Array<OfferDriverField & { set: boolean; value: string | null }>;
  stores: Array<{ id: string; name: string; linked: boolean; externalStoreId: string | null }>;
}

export interface OfferIntegrationInput {
  organizationId: string;
  name: string;
  driver: string;
  credentials: Record<string, string>;
  active: boolean;
  settings?: OfferRerunSettings;
}

/** Omitted credential fields, and empty secrets, keep the saved value. */
export interface OfferIntegrationPatch {
  name?: string;
  credentials?: Record<string, string>;
  active?: boolean;
  settings?: OfferRerunSettings;
}

/** SUCCEEDED: it quoted · DECLINED: it won't quote this lead (staff set the max) · FAILED: an error (retried). */
export type OfferRunStatus = "SUCCEEDED" | "DECLINED" | "FAILED";
/** Why it ran: the lead's first API offer, or a re-run (integration settings). */
export type OfferRunTrigger = "INITIAL" | "MILEAGE_CHANGED" | "OFFER_EXPIRED" | "RETRY" | "FOLLOW_UP";
export type OfferRunStepStatus = "OK" | "WARN" | "FAILED" | "SKIPPED";

/** One step of a run, in order. `detail` is sanitized server-side (never secrets). */
export interface OfferRunStep {
  key: string;
  label: string;
  status: OfferRunStepStatus;
  at: string;
  durationMs: number | null;
  message: string | null;
  detail: Record<string, unknown> | null;
}

/** One call of an offer integration for one lead (a retried job adds a run per attempt). */
export interface OfferRun {
  id: string;
  integrationId: string;
  /** The integration's name (the lead's log lists every integration). */
  integration?: EntityRef;
  driver: string;
  trigger: OfferRunTrigger;
  attempt: number;
  status: OfferRunStatus;
  /** What the provider quoted (offer, or valuation average). */
  amountCents: number | null;
  /** The max offer it allows under the store's policy. */
  ceilingCents: number | null;
  /** Its quote became the lead's max offer. */
  applied: boolean;
  reference: string | null;
  error: string | null;
  store: EntityRef | null;
  opportunityId: string | null;
  vehicle: string | null;
  stepCount: number;
  failedStep: string | null;
  startedAt: string;
  finishedAt: string;
  durationMs: number;
}

export interface OfferRunDetail extends OfferRun {
  valuationId: string | null;
  steps: OfferRunStep[];
}

export interface OfferRunFilters {
  status?: OfferRunStatus;
  trigger?: OfferRunTrigger;
  storeId?: string;
  opportunityId?: string;
}

/** An organization's offer integration as one store sees it (store page → integrations). */
export interface StoreOfferIntegration {
  integrationId: string;
  name: string;
  driverLabel: string;
  active: boolean;
  linked: boolean;
  externalStoreId: string | null;
  externalStoreIdField: OfferDriver["externalStoreId"];
}

// ── purchases ──────────────────────────────────────────────────────────────
export type PurchaseStatus = "ACQUIRED" | "IN_RECON" | "FRONTLINE" | "SOLD" | "WHOLESALED";

export interface PurchaseRow {
  id: string;
  vehicle: { id: string; title: string | null; vin: string | null } | null;
  organization: EntityRef;
  store: EntityRef;
  seller: { id: string; name: string | null } | null;
  source: OpportunitySource;
  amountCents: number;
  buyer: EntityRef | null;
  purchasedAt: string;
  status: PurchaseStatus | null;
  opportunityId: string;
}

/** lead → opportunity → conversation → offer → appointment → purchase */
export interface PurchaseTrace {
  purchaseId: string;
  steps: Array<{
    kind: "lead" | "opportunity" | "conversation" | "offer" | "appointment" | "purchase";
    id: string | null;
    label: string;
    at: string | null;
  }>;
}

export interface PurchaseFilters {
  organizationId?: string;
  storeId?: string;
  source?: OpportunitySource;
  from?: string;
  to?: string;
}

// ── analytics ──────────────────────────────────────────────────────────────
export const ANALYTICS_SECTIONS = ["funnel", "organizations", "stores", "sources", "ai", "attribution", "queues", "integrations"] as const;
export type AnalyticsSection = (typeof ANALYTICS_SECTIONS)[number];

export interface AnalyticsReport {
  section: AnalyticsSection;
  range: DateRange;
  /** Funnel steps (section = funnel). Missing counts are null, never estimated. */
  funnel?: Array<{ key: string; label: string; count: number | null }>;
  /** Tabular sections: columns declared by the server; the UI renders whatever it returns. */
  table?: {
    columns: Array<{ key: string; label: string; kind: "text" | "count" | "percent" | "money" | "ms" }>;
    rows: Array<Record<string, string | number | null>>;
  };
}

// ── audit ──────────────────────────────────────────────────────────────────
export type AuditResult = "SUCCESS" | "FAILURE" | "DENIED";
export type ActorType = "ADMIN" | "USER" | "ROGUE" | "SYSTEM";

export interface AuditEvent {
  id: string;
  at: string;
  actor: { type: ActorType; id: string | null; name: string | null };
  organization: EntityRef | null;
  store: EntityRef | null;
  action: string;
  resource: { type: string; id: string | null; label: string | null };
  result: AuditResult;
  /** With `details`: where it came from, and what changed. */
  source?: string;
  before?: Record<string, unknown> | null;
  after?: Record<string, unknown> | null;
  metadata?: Record<string, unknown>;
}

export interface AuditEventDetail extends AuditEvent {
  source: string;
  reason: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  metadata: Record<string, unknown>;
  /** Field paths the backend redacted for this viewer. */
  redactedFields: string[];
}

export interface AuditFilters {
  /** "true": items carry source, before, after and metadata (what changed). */
  details?: "true";
  actor?: string;
  actorType?: ActorType;
  organizationId?: string;
  storeId?: string;
  action?: string;
  resourceType?: string;
  resourceId?: string;
  result?: AuditResult;
  from?: string;
  to?: string;
}

// ── search ─────────────────────────────────────────────────────────────────
export const SEARCH_GROUPS = ["organizations", "stores", "users", "opportunities", "sellers", "vehicles", "conversations", "aiRuns", "purchases"] as const;
export type SearchGroup = (typeof SEARCH_GROUPS)[number];
export interface SearchHit {
  id: string;
  title: string;
  subtitle: string;
  /** Where the hit opens (seller/vehicle hits open their opportunity). */
  target: { kind: "organization" | "store" | "user" | "opportunity" | "conversation" | "aiRun" | "purchase"; id: string };
}
export type SearchResults = Record<SearchGroup, SearchHit[]>;

// ── realtime ───────────────────────────────────────────────────────────────
/** Admin event names the UI knows how to apply. Only subscribed when the contract defines them. */
export const ADMIN_EVENT_TYPES = [
  "system.health_changed",
  "queue.updated",
  "ai_run.created",
  "ai_run.updated",
  "integration.status_changed",
  "opportunity.updated",
  "purchase.created",
] as const;
export type AdminEventType = (typeof ADMIN_EVENT_TYPES)[number];

export interface AdminEvent<T = Record<string, unknown>> {
  id: string;
  type: AdminEventType;
  occurredAt: string;
  data: T;
}

// ── Rogue's prompt: the parts admins edit (global and per organization) ─────
export type PromptSectionSource = "DEFAULT" | "GLOBAL" | "ORGANIZATION";
export interface PromptSection {
  key: string;
  label: string;
  description: string;
  multiline: boolean;
  maxLength: number;
  /** Can be set to nothing (additional instructions). */
  allowEmpty: boolean;
  /** Built-in text. */
  default: string;
  /** The global override (null: the default applies). */
  global: string | null;
  /** This scope's own override (null: inherits). */
  override: string | null;
  /** What Rogue uses (for an organization: its override, else global, else the default). */
  effective: string;
  source: PromptSectionSource;
}
export interface PromptSettings {
  scope: "GLOBAL" | "ORGANIZATION";
  organization: EntityRef | null;
  updatedAt: string | null;
  updatedBy: EntityRef | null;
  sections: PromptSection[];
}

// ── follow-ups (rules admins edit) ─────────────────────────────────────────
export type FollowupRuleAction = "CHECK_IN" | "OFFER_AGAIN" | "RERUN_API_OFFER";

/** A follow-up rule: when the seller has been quiet this long (and the lead matches), Rogue acts. */
export interface FollowupRule {
  /** Lowercase letters, digits and dashes; unique in the list. */
  id: string;
  name: string;
  enabled: boolean;
  when: {
    /** Lead stages (none: any stage before an agreement). */
    stages: string[];
    /** MADE: an offer was texted; NONE: no offer yet; ANY: either. */
    offer: "ANY" | "MADE" | "NONE";
    /** SET: the lead has a max offer; NOT_SET: not yet; ANY: either. */
    maxOffer: "ANY" | "SET" | "NOT_SET";
    /** Days of silence since Rogue's last text, before the first attempt. */
    silentDays: number;
  };
  action: FollowupRuleAction;
  maxAttempts: number;
  /** Days between attempts after the first. */
  repeatEveryDays: number;
  /** Extra guidance for Rogue's text. */
  instructions: string | null;
  /** After the last attempt, close the lead as unresponsive this many days later (null: never). */
  markUnresponsiveAfterDays: number | null;
}

export interface FollowupSettingsValue {
  /** Off: no follow-ups at all for this scope. */
  enabled: boolean;
  /** The seller asked to talk later ("next Friday"). */
  sellerRequested: { enabled: boolean; defaultTime: string; defaultDays: number; maxDaysAhead: number };
  /** At the max offer and the seller won't take it: Rogue checks back every N days, or a person takes over. */
  priceGap: { action: "FOLLOW_UP" | "HANDOFF"; everyDays: number };
  /** In order: the first enabled rule that matches (with attempts left) applies. */
  rules: FollowupRule[];
}

/** GET /admin/v1/followup-settings and /admin/v1/organizations/{id}/followup-settings */
export interface FollowupSettingsView {
  scope: "GLOBAL" | "ORGANIZATION";
  organization: EntityRef | null;
  /** Where the effective settings come from. */
  source: "DEFAULT" | "GLOBAL" | "ORGANIZATION";
  /** This scope has its own settings (an organization doesn't inherit global). */
  custom: boolean;
  settings: FollowupSettingsValue;
  /** What applies without its own: global (an organization) or the built-in defaults (global). */
  inherited: FollowupSettingsValue;
  defaults: FollowupSettingsValue;
  options: { stages: string[]; actions: FollowupRuleAction[] };
  updatedAt: string | null;
  updatedBy: EntityRef | null;
}

/** POST /admin/v1/followups/catch-up: leads that went quiet before follow-ups. */
export interface FollowupCatchUpInput {
  /** Null: every organization. */
  organizationId: string | null;
  quietForAtMostDays: number;
  perStorePerDay: number;
  dryRun: boolean;
}

export interface FollowupCatchUpResult {
  dryRun: boolean;
  quietForAtMostDays: number;
  perStorePerDay: number;
  /** Quiet leads looked at; truncated: more than one run handles (run it again). */
  leads: number;
  truncated: boolean;
  /** Follow-ups planned (a preview: that would be). */
  followups: number;
  /** Leads no rule applies to. */
  noRule: number;
  firstAt: string | null;
  lastAt: string | null;
  byRule: Array<{ name: string; count: number }>;
  stores: Array<{ store: EntityRef; organization: EntityRef | null; followups: number; firstAt: string; lastAt: string }>;
}

/** GET /admin/v1/opportunities/{id}/offer-runs: one lead's Offer API log (every integration). */
export interface OpportunityOfferRuns extends Page<OfferRun> {
  /** An API offer is queued or running now. */
  pending: { valuationId: string; since: string; rerun: string | null } | null;
}

/** GET /admin/v1/offer-api/drivers */
export interface OfferDriverCatalog {
  items: OfferDriver[];
  /** An organization can have only one offer API integration (production). */
  onePerOrganization: boolean;
}
